/** D-14 — account vs global retail context for More IA. */

/**
 * Last overflow destination the user chose from Více — keeps the row marked
 * across auth-gate navigations where platform return-target cannot store
 * `/account/*` paths. Session storage survives shell remounts / HMR.
 */
const MORE_DESTINATION_SESSION_KEY = 'customer:moreDestination';
let memoryMoreDestination: string | null = null;

function readSessionMoreDestination(): string | null {
  if (typeof sessionStorage === 'undefined') {
    return null;
  }
  try {
    const raw = sessionStorage.getItem(MORE_DESTINATION_SESSION_KEY);
    if (raw === null) {
      return null;
    }
    const trimmed = raw.trim();
    return trimmed.length > 0 ? trimmed : null;
  } catch {
    return null;
  }
}

function writeSessionMoreDestination(to: string | null): void {
  if (typeof sessionStorage === 'undefined') {
    return;
  }
  try {
    if (to === null) {
      sessionStorage.removeItem(MORE_DESTINATION_SESSION_KEY);
    } else {
      sessionStorage.setItem(MORE_DESTINATION_SESSION_KEY, to);
    }
  } catch {
    // Ignore quota / private-mode failures — in-memory still works.
  }
}

export function setMemoryMoreDestination(to: string | null): void {
  memoryMoreDestination =
    to !== null && to.trim().length > 0 ? to.trim() : null;
  writeSessionMoreDestination(memoryMoreDestination);
}

export function peekMemoryMoreDestination(): string | null {
  return memoryMoreDestination ?? readSessionMoreDestination();
}

export function clearMemoryMoreDestination(): void {
  memoryMoreDestination = null;
  writeSessionMoreDestination(null);
}

export function tenantBase(tenantCode: string): string {
  return `/${encodeURIComponent(tenantCode)}`;
}

/** Wave D — platform account hubs (also match legacy `/{tenant}/account/**`). */
export function isAccountRoute(pathname: string, tenantCode: string): boolean {
  if (pathname === '/account' || pathname.startsWith('/account/')) {
    return true;
  }
  const prefix = `${tenantBase(tenantCode)}/account`;
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

/** Wave D — platform `/more` plus legacy `/{tenant}/more`. */
export function isMoreRoute(pathname: string, tenantCode: string): boolean {
  if (pathname === '/more' || pathname.startsWith('/more/')) {
    return true;
  }
  const morePath = `${tenantBase(tenantCode)}/more`;
  return pathname === morePath || pathname.startsWith(`${morePath}/`);
}

function normalizeTrailingSlash(path: string): string {
  if (path.length > 1 && path.endsWith('/')) {
    return path.slice(0, -1);
  }
  return path;
}

/**
 * Platform chrome segments — path is already relative (do not strip `/account`).
 */
const PLATFORM_RELATIVE_FIRST_SEGMENTS = new Set([
  'account',
  'more',
  'browse',
  'shops',
  'favorites',
  'orgs',
  'sign-in',
  'sign-up',
  'orders',
  'donate',
  'help',
  'forgot-password',
  'reset-password',
  'confirm-email',
  'onboarding',
  'login',
  'checkout',
  'recovery',
  'handoff',
  'post-kiosk',
  'post-kiosk-failure',
  'unavailable',
  'card',
  'pickup',
  'scan',
  'shop',
  'home',
]);

/**
 * Strip the first path segment (`/:tenantCode`) so jwt-home menu `to`s still
 * match when the URL tenant slug differs (or trailing slashes vary).
 * Wave D platform paths (`/account/**`, `/more`, …) are returned as-is.
 */
export function tenantRelativeMorePath(pathname: string): string {
  const normalized = normalizeTrailingSlash(pathname);
  if (normalized === '/' || normalized.length === 0) {
    return '/';
  }
  const firstSeg = normalized.slice(1).split('/')[0] ?? '';
  if (PLATFORM_RELATIVE_FIRST_SEGMENTS.has(firstSeg)) {
    return normalized;
  }
  const slash = normalized.indexOf('/', 1);
  if (slash === -1) {
    return '/';
  }
  return normalized.slice(slash);
}

/**
 * iOS-style More row selection: exact account home; prefix-match nested routes.
 * Tenant-agnostic so `/home/account/payments` matches a `/browse/…` URL after nav.
 */
export function isMoreRowActive(pathname: string, to: string | undefined): boolean {
  if (to === undefined || to.length === 0) {
    return false;
  }
  const pathRel = tenantRelativeMorePath(pathname);
  const targetRel = tenantRelativeMorePath(to);

  if (targetRel === '/account' || targetRel === '/account/home') {
    return pathRel === '/account' || pathRel === '/account/home';
  }

  if (pathRel === targetRel) {
    return true;
  }
  return pathRel.startsWith(`${targetRel}/`);
}

/**
 * Keep the clicked overflow row marked while auth-gating (return target) or during
 * the brief optimistic window before the route settles.
 */
export function resolveMoreRowActive(input: {
  readonly pathname: string;
  readonly to: string | undefined;
  readonly pendingPath?: string | null;
  readonly optimisticPath?: string | null;
}): boolean {
  const { pathname, to, pendingPath, optimisticPath } = input;
  if (isMoreRowActive(pathname, to)) {
    return true;
  }
  if (
    optimisticPath !== undefined &&
    optimisticPath !== null &&
    optimisticPath.length > 0 &&
    isMoreRowActive(optimisticPath, to)
  ) {
    return true;
  }
  if (
    pendingPath !== undefined &&
    pendingPath !== null &&
    pendingPath.length > 0 &&
    isMoreRowActive(pendingPath, to)
  ) {
    return true;
  }
  return false;
}
